/**
 * Deterministic raster filters and deformations (Filter and Edit menus, the
 * High Pass, Match Color, Content-Aware Scale, Deform and Apply Image
 * dialogs, and Invert layer colors).
 *
 * Every command names its layers explicitly (`layer`, `source`, ...) instead
 * of reading the active layer, and computes its pixels in `compile` with the
 * same functions the dialogs used, so the native command (and its history
 * label) is exactly the one the dialog executed. Where a dialog read the
 * document selection implicitly, the command takes `selection`: 'document'
 * (the default, the dialog's behaviour), 'none', or explicit regions.
 */
import type { FieldChange, LayerId, Ref } from '@maipo/agent-protocol';
import { applyImageStores, grayscaleStore, ReplaceMaskPixelsCommand, thresholdRegions, type ApplyImageBlend, type ApplyImageChannel, type ApplyImageSettings } from '../../core/apply-image';
import { alphaChannels, SetAlphaChannelsCommand } from '../../core/channels';
import { AddLayerCommand, SelectionCommand } from '../../core/commands';
import { deformRaster, type RasterDeformSettings } from '../../core/deform';
import { transformPoint, type Matrix, type Rect } from '../../core/geometry';
import { createId, createRasterLayer, findLayer, findLayerParent, layerDocumentTransform, type EditorDocument, type Layer, type RasterLayer } from '../../core/model';
import { EMPTY_SELECTION, hasSelection, pointIsSelected, projectRegionsToDocument, type SelectionState } from '../../core/selection';
import { createHighPassSmartFilter, SetSmartFiltersCommand, smartFilters } from '../../core/smart-filters';
import { isSmartObject } from '../../core/smart-objects';
import { ReplaceRasterCommand } from '../../core/template';
import { RasterTileStore } from '../../core/tiles';
import { contentAwareScalePixels } from '../../filters/content-aware-scale';
import { applyHighPassPixels } from '../../filters/high-pass';
import { matchColorPixels } from '../../filters/match-color';
import { invertImage } from '../../tooling/pixel-ops';
import type { Diagnostic, Footprint, ValidateContext } from '../registry';
import { ancestorIds, checkContainerUnlocked, checkUnlocked, error, layerFor, parentKey, requireLayer, resolveRef, staticIds } from '../intents/common';
import { finiteCost, nativePixelCost, rasterPixels } from './cost';
import { defineCommand, DOCUMENT_RESOURCES } from './registry';

/** Pixel limit the destructive dialogs enforce (High Pass, Match Color, deformations). */
export const DESTRUCTIVE_FILTER_MAX_PIXELS = 16_000_000;
/** Alpha channels a document keeps (Channels panel and Apply Image). */
export const MAX_ALPHA_CHANNELS = 8;

/** 'document' = the document's current selection; 'none' = ignore it; or explicit regions in document pixels. */
export type SelectionParam = 'document' | 'none' | { regions: Rect[]; inverted?: boolean };

function resolveSelection(document: EditorDocument, param: SelectionParam | undefined): SelectionState {
  if (param === undefined || param === 'document') return document.selection;
  if (param === 'none') return EMPTY_SELECTION;
  return { regions: param.regions.map((region) => ({ ...region })), inverted: param.inverted ?? false };
}

/**
 * Typed-array passes over the layer's pixels (see ./cost); copies into and
 * out of `ImageData` count. A selection adds a native per-pixel loop that
 * maps every pixel to document space and tests it (`clipCost`).
 */
const FILTER_PASSES = Object.freeze({
  /** Three channels, each a separable box blur (horizontal and vertical sums) plus the high-pass write. */
  highPass: 16,
  /** Target: copy in, match, copy out; source: copy in, statistics. */
  matchColorTarget: 3,
  matchColorSource: 2,
  /** Per inserted or removed seam: energy, dynamic programming and removal over the whole raster. */
  contentAwareScalePerSeam: 4,
  /** Inverse mapping and bilinear sampling per output pixel. */
  deform: 8,
  invert: 3,
  /** Both operands read, the result written. */
  applyImage: 3,
});

function clipCost(document: EditorDocument, pixels: number, param: SelectionParam | undefined): number {
  const selection = resolveSelection(document, param);
  return hasSelection(selection) ? pixels * nativePixelCost(selection) : 0;
}

function selectionReads(param: SelectionParam | undefined): string[] {
  return param === undefined || param === 'document' ? [DOCUMENT_RESOURCES.selection] : [];
}

/** Writes `writes` (and reads their ancestors for lock checks), reads `reads` and any resource keys. */
function pixelFootprint(document: EditorDocument, aliases: ReadonlyMap<string, LayerId>, writes: readonly Ref[], reads: readonly Ref[] = [], resources: readonly string[] = []): Footprint {
  const written = staticIds(writes, aliases);
  const read = staticIds(reads, aliases);
  return {
    reads: [...new Set([...written, ...written.flatMap((id) => ancestorIds(document, id)), ...read, ...resources])],
    writes: written,
    structural: false,
    document: false,
  };
}

function rasterLayer(document: EditorDocument, ref: Ref, context: ValidateContext, diagnostics: Diagnostic[], what: string): RasterLayer | null {
  const layer = layerFor(document, ref, context, diagnostics);
  if (!layer) return null;
  if (layer.kind !== 'raster') {
    diagnostics.push(error('UNSUPPORTED_FOR_KIND', `${what} needs a raster layer; ${layer.id} is a ${layer.kind} layer.`, layer.id));
    return null;
  }
  return layer;
}

function requireRaster(document: EditorDocument, ref: Ref, aliases: ReadonlyMap<string, LayerId>): RasterLayer {
  const layer = requireLayer(document, ref, aliases);
  if (layer.kind !== 'raster') throw new Error(`Layer ${layer.id} is not a raster layer.`);
  return layer;
}

function checkPixelLimit(layer: RasterLayer, message: string, diagnostics: Diagnostic[]): void {
  if (layer.raster.width * layer.raster.height > DESTRUCTIVE_FILTER_MAX_PIXELS) diagnostics.push(error('LIMIT_EXCEEDED', message, layer.id));
}

/** Pixel-space test of a selection for `layer`, exact under rotation and skew. */
function documentTransform(document: EditorDocument, layer: Layer): Matrix {
  return layerDocumentTransform(document.root, layer.id) ?? layer.transform;
}

function describePixels(command: string) {
  return (_document: EditorDocument, params: { layer: Ref }, aliases: ReadonlyMap<string, LayerId>): FieldChange[] => [
    { layer: resolveRef(params.layer, aliases) ?? params.layer, path: 'raster.pixels', before: null, after: { command } },
  ];
}

// --- High Pass -------------------------------------------------------------

export interface HighPassParams { layer: Ref; radius?: number }

export const highPass = defineCommand<HighPassParams>({
  id: 'filters.highPass',
  group: 'filters',
  backs: ['action.high-pass', 'dialog.high-pass.raster', 'dialog.high-pass.smart', 'panel.layers.smart-filter-add-high-pass'],
  label: (params, document) => isSmartObject(findLayer(document.root, params.layer)) ? 'Add High Pass Smart Filter' : 'High Pass',
  // A Smart Object only gets a filter entry (rendered later, viewport-bounded): rasterPixels is 0 for it.
  cost: (params, document) => finiteCost(rasterPixels(document, params.layer) * FILTER_PASSES.highPass),
  footprint: (document, params, aliases) => pixelFootprint(document, aliases, [params.layer]),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, params.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (!isSmartObject(layer) && layer.kind !== 'raster') {
      diagnostics.push(error('UNSUPPORTED_FOR_KIND', 'High Pass requires a raster layer or embedded Smart Object.', layer.id));
      return diagnostics;
    }
    if (layer.kind === 'raster') checkPixelLimit(layer, 'Destructive High Pass is limited to 16 megapixels; convert to a Smart Object for viewport-bounded rendering.', diagnostics);
    checkUnlocked(document, layer, 'pixels', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireLayer(document, params.layer, aliases);
    const radius = params.radius ?? 4;
    if (isSmartObject(layer)) return [new SetSmartFiltersCommand(layer.id, [...smartFilters(layer), createHighPassSmartFilter(radius)], 'Add High Pass Smart Filter')];
    if (layer.kind !== 'raster') throw new Error('High Pass requires a raster layer or embedded Smart Object.');
    const image = layer.raster.toImageData();
    applyHighPassPixels(image.data, image.width, image.height, radius);
    return [new ReplaceRasterCommand(layer.id, RasterTileStore.fromImageData(image), 'High Pass')];
  },
  describe: describePixels('filters.highPass'),
});

// --- Match Color -----------------------------------------------------------

export interface MatchColorParams {
  layer: Ref;
  source: Ref;
  /** Percent, 0-200 (default 100). */
  luminance?: number;
  /** Percent, 0-200 (default 100). */
  colorIntensity?: number;
  /** Percent, 0-100 (default 0). */
  fade?: number;
  neutralize?: boolean;
  selection?: SelectionParam;
}

export const matchColor = defineCommand<MatchColorParams>({
  id: 'filters.matchColor',
  agentDefaults: { selection: 'none' },
  group: 'filters',
  backs: ['action.match-color', 'dialog.match-color'],
  label: () => 'Match Color',
  cost: (params, document) => {
    const target = rasterPixels(document, params.layer);
    return finiteCost(target * FILTER_PASSES.matchColorTarget + rasterPixels(document, params.source) * FILTER_PASSES.matchColorSource + clipCost(document, target, params.selection));
  },
  footprint: (document, params, aliases) => pixelFootprint(document, aliases, [params.layer], [params.source], selectionReads(params.selection)),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const target = rasterLayer(document, params.layer, context, diagnostics, 'Match Color');
    const sourceLayer = layerFor(document, params.source, context, diagnostics);
    if (sourceLayer && sourceLayer.kind !== 'raster') diagnostics.push(error('UNSUPPORTED_FOR_KIND', 'Choose a raster source layer.', sourceLayer.id));
    if (!target) return diagnostics;
    checkPixelLimit(target, 'Match Color is limited to raster layers up to 16 megapixels.', diagnostics);
    checkUnlocked(document, target, 'pixels', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const target = requireRaster(document, params.layer, aliases);
    const source = requireRaster(document, params.source, aliases);
    const selection = resolveSelection(document, params.selection);
    const sourceImage = source.raster.toImageData();
    const targetImage = target.raster.toImageData();
    const matched = matchColorPixels(sourceImage.data, targetImage.data, {
      luminance: (params.luminance ?? 100) / 100,
      colorIntensity: (params.colorIntensity ?? 100) / 100,
      fade: (params.fade ?? 0) / 100,
      neutralize: params.neutralize ?? false,
    });
    if (hasSelection(selection)) {
      // Unselected pixels keep their original values.
      const transform = documentTransform(document, target);
      for (let y = 0; y < targetImage.height; y += 1) {
        for (let x = 0; x < targetImage.width; x += 1) {
          if (pointIsSelected(selection, transformPoint(transform, { x: x + 0.5, y: y + 0.5 }))) continue;
          const offset = (y * targetImage.width + x) * 4;
          matched.set(targetImage.data.subarray(offset, offset + 4), offset);
        }
      }
    }
    return [new ReplaceRasterCommand(target.id, RasterTileStore.fromImageData({ data: matched, width: targetImage.width, height: targetImage.height }), 'Match Color')];
  },
  describe: describePixels('filters.matchColor'),
});

// --- Content-Aware Scale ---------------------------------------------------

export interface ContentAwareScaleParams { layer: Ref; width: number; height: number; selection?: SelectionParam }

/** The limits contentAwareScalePixels enforces, as diagnostics before anything runs. */
function contentAwareScaleLimits(layer: RasterLayer, targetWidth: number, targetHeight: number): Diagnostic | null {
  const { width, height } = layer.raster;
  if (![targetWidth, targetHeight].every((value) => Number.isInteger(value) && value > 0)) return error('COMMAND_REJECTED', 'Content-Aware Scale dimensions are invalid.', layer.id);
  if (width * height > 2_000_000 || targetWidth * targetHeight > 4_000_000) return error('LIMIT_EXCEEDED', 'Content-Aware Scale is limited to 2 megapixel sources and 4 megapixel outputs.', layer.id);
  if (Math.abs(targetWidth - width) > 256 || Math.abs(targetHeight - height) > 256) return error('LIMIT_EXCEEDED', 'Content-Aware Scale is limited to 256 inserted or removed seams per axis; use Resize Image for larger changes.', layer.id);
  if (width * height * (Math.abs(targetWidth - width) + Math.abs(targetHeight - height)) > 120_000_000) return error('LIMIT_EXCEEDED', 'This Content-Aware Scale request requires too much seam analysis. Use smaller steps or Resize Image.', layer.id);
  if (targetWidth < 2 || targetHeight < 2) return error('COMMAND_REJECTED', 'Content-Aware Scale output must be at least 2 × 2 pixels.', layer.id);
  return null;
}

export const contentAwareScale = defineCommand<ContentAwareScaleParams>({
  id: 'filters.contentAwareScale',
  agentDefaults: { selection: 'none' },
  group: 'filters',
  backs: ['action.content-aware-scale', 'dialog.content-aware-scale'],
  label: () => 'Content-Aware Scale',
  cost: (params, document) => {
    const layer = findLayer(document.root, params.layer);
    const pixels = rasterPixels(document, params.layer);
    const seams = layer?.kind === 'raster' ? Math.abs(Math.round(params.width) - layer.raster.width) + Math.abs(Math.round(params.height) - layer.raster.height) : 0;
    return finiteCost(pixels * (2 + seams * FILTER_PASSES.contentAwareScalePerSeam) + clipCost(document, pixels, params.selection));
  },
  footprint: (document, params, aliases) => pixelFootprint(document, aliases, [params.layer], [], selectionReads(params.selection)),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = rasterLayer(document, params.layer, context, diagnostics, 'Content-Aware Scale');
    if (!layer) return diagnostics;
    const width = Math.round(params.width);
    const height = Math.round(params.height);
    const limit = contentAwareScaleLimits(layer, width, height);
    if (limit) diagnostics.push(limit);
    checkUnlocked(document, layer, 'pixels', diagnostics, context.policy);
    // A raster mask must match its layer's size; resizing under it would leave a document that cannot be saved.
    if (layer.mask && (width !== layer.raster.width || height !== layer.raster.height)) {
      diagnostics.push(error('COMMAND_REJECTED', 'Content-Aware Scale changes the layer size; apply or delete the layer mask first.', layer.id));
    }
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireRaster(document, params.layer, aliases);
    const selection = resolveSelection(document, params.selection);
    const source = layer.raster.toImageData();
    const protect = new Uint8Array(source.width * source.height);
    if (hasSelection(selection)) {
      const transform = documentTransform(document, layer);
      for (let y = 0; y < source.height; y += 1) for (let x = 0; x < source.width; x += 1) {
        if (pointIsSelected(selection, transformPoint(transform, { x: x + 0.5, y: y + 0.5 }))) protect[y * source.width + x] = 255;
      }
    }
    const result = contentAwareScalePixels(source.data, source.width, source.height, Math.round(params.width), Math.round(params.height), protect);
    const raster = RasterTileStore.fromImageData({ data: result.pixels, width: result.width, height: result.height });
    return [new ReplaceRasterCommand(layer.id, raster, 'Content-Aware Scale')];
  },
  describe: describePixels('filters.contentAwareScale'),
});

// --- Liquify, Puppet Warp, Warp / Perspective -------------------------------

const DEFORM_LIMIT = 'Deformation is limited to 16 megapixels per layer in this release.';

function deformCommand<P extends { layer: Ref }>(options: {
  id: string;
  backs: readonly string[];
  label: string;
  settings(layer: RasterLayer, params: P): RasterDeformSettings;
}) {
  return defineCommand<P>({
    id: options.id,
    group: 'filters',
    backs: options.backs,
    label: () => options.label,
    cost: (params, document) => finiteCost(rasterPixels(document, params.layer) * FILTER_PASSES.deform),
    footprint: (document, params, aliases) => pixelFootprint(document, aliases, [params.layer]),
    validate: (document, params, context) => {
      const diagnostics: Diagnostic[] = [];
      const layer = rasterLayer(document, params.layer, context, diagnostics, options.label);
      if (!layer) return diagnostics;
      checkPixelLimit(layer, DEFORM_LIMIT, diagnostics);
      checkUnlocked(document, layer, 'pixels', diagnostics, context.policy);
      return diagnostics;
    },
    compile: (document, params, _prepared, aliases) => {
      const layer = requireRaster(document, params.layer, aliases);
      return [new ReplaceRasterCommand(layer.id, deformRaster(layer.raster, options.settings(layer, params)), options.label)];
    },
    describe: describePixels(options.id),
  });
}

/** Positions and moves are percentages of the layer's raster (as in the dialog). */
export interface LiquifyParams { layer: Ref; brush?: 'warp' | 'pucker' | 'bloat'; centerX?: number; centerY?: number; radius?: number; moveX?: number; moveY?: number; strength?: number }
export interface PuppetWarpParams { layer: Ref; pinX?: number; pinY?: number; radius?: number; moveX?: number; moveY?: number }
export interface WarpPerspectiveParams { layer: Ref; bendX?: number; bendY?: number; perspectiveX?: number; perspectiveY?: number }

export const liquify = deformCommand<LiquifyParams>({
  id: 'filters.liquify',
  backs: ['action.liquify', 'dialog.deform.liquify'],
  label: 'Liquify',
  settings: ({ raster }, params) => ({
    mode: 'liquify',
    brush: params.brush ?? 'warp',
    centerX: raster.width * (params.centerX ?? 50) / 100,
    centerY: raster.height * (params.centerY ?? 50) / 100,
    radius: Math.max(raster.width, raster.height) * (params.radius ?? 35) / 100,
    deltaX: raster.width * (params.moveX ?? 12) / 100,
    deltaY: raster.height * (params.moveY ?? 0) / 100,
    strength: (params.strength ?? 70) / 100,
  }),
});

export const puppetWarp = deformCommand<PuppetWarpParams>({
  id: 'filters.puppetWarp',
  backs: ['action.puppet-warp', 'dialog.deform.puppet'],
  label: 'Puppet Warp',
  settings: ({ raster }, params) => ({
    mode: 'puppet',
    pinX: raster.width * (params.pinX ?? 50) / 100,
    pinY: raster.height * (params.pinY ?? 50) / 100,
    deltaX: raster.width * (params.moveX ?? 12) / 100,
    deltaY: raster.height * (params.moveY ?? 0) / 100,
    radius: Math.max(raster.width, raster.height) * (params.radius ?? 35) / 100,
  }),
});

export const warpPerspective = deformCommand<WarpPerspectiveParams>({
  id: 'filters.warpPerspective',
  backs: ['action.warp-perspective', 'dialog.deform.warp-perspective'],
  label: 'Free Transform · Warp / Perspective',
  settings: (_layer, params) => ({
    mode: 'warp-perspective',
    bendX: (params.bendX ?? 12) / 100,
    bendY: (params.bendY ?? 0) / 100,
    perspectiveX: (params.perspectiveX ?? 20) / 100,
    perspectiveY: (params.perspectiveY ?? 0) / 100,
  }),
});

// --- Invert layer colors ---------------------------------------------------

export const invertPixels = defineCommand<{ layer: Ref }>({
  id: 'pixels.invert',
  group: 'pixels',
  backs: ['action.invert-layer-colors'],
  label: () => 'Invert colors',
  cost: (params, document) => finiteCost(rasterPixels(document, params.layer) * FILTER_PASSES.invert),
  footprint: (document, params, aliases) => pixelFootprint(document, aliases, [params.layer]),
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = rasterLayer(document, params.layer, context, diagnostics, 'Invert colors');
    if (layer) checkUnlocked(document, layer, 'pixels', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireRaster(document, params.layer, aliases);
    const image = layer.raster.toImageData();
    invertImage(image);
    return [new ReplaceRasterCommand(layer.id, RasterTileStore.fromImageData(image), 'Invert colors')];
  },
  describe: describePixels('pixels.invert'),
});

// --- Apply Image / Calculations --------------------------------------------

export type ApplyImageOutput = 'layer' | 'new-layer' | 'mask' | 'selection' | 'alpha-channel';

export interface ApplyImageParams {
  /** The target layer: its pixels (output 'layer') or mask (output 'mask'); operand B unless `blendLayer` is given. */
  layer: Ref;
  /** Operand A. */
  source: Ref;
  /** Operand B (default `layer`); a different layer makes this Calculations. */
  blendLayer?: Ref;
  output: ApplyImageOutput;
  channel?: ApplyImageChannel;
  invert?: boolean;
  blendChannel?: ApplyImageChannel;
  blendInvert?: boolean;
  mode?: ApplyImageBlend;
  /** Percent, 0-100 (default 100). */
  opacity?: number;
  /** Add and Subtract only (default 1). */
  scale?: number;
  /** Add and Subtract only (default 0). */
  offset?: number;
  selection?: SelectionParam;
}

function applyImageSettings(params: ApplyImageParams): ApplyImageSettings {
  const mode = params.mode ?? 'multiply';
  const scalable = mode === 'add' || mode === 'subtract';
  return {
    mode,
    channel: params.channel ?? 'luminosity',
    invert: params.invert ?? false,
    opacity: (params.opacity ?? 100) / 100,
    targetChannel: params.blendChannel ?? 'rgb',
    targetInvert: params.blendInvert ?? false,
    scale: scalable ? params.scale ?? 1 : 1,
    offset: scalable ? params.offset ?? 0 : 0,
  };
}

const blendRef = (params: ApplyImageParams): Ref => params.blendLayer ?? params.layer;

export const applyImage = defineCommand<ApplyImageParams>({
  id: 'filters.applyImage',
  createsLayer: (params) => params.output === 'new-layer',
  agentDefaults: { selection: 'none' },
  group: 'filters',
  cost: (params, document) => {
    const blend = rasterPixels(document, blendRef(params));
    return finiteCost((rasterPixels(document, params.source) + blend) * FILTER_PASSES.applyImage + clipCost(document, blend, params.selection));
  },
  backs: ['action.apply-image', 'dialog.apply-image.layer', 'dialog.apply-image.new', 'dialog.apply-image.mask', 'dialog.apply-image.selection', 'dialog.apply-image.alpha'],
  label: (params, document) => {
    switch (params.output) {
      case 'layer': return 'Apply Image';
      case 'new-layer': return `Add ${findLayer(document.root, blendRef(params))?.name ?? 'layer'} ${params.mode ?? 'multiply'}`;
      case 'mask': return 'Apply Image to mask';
      case 'selection': return 'Apply Image to selection';
      default: return 'Apply Image to alpha channel';
    }
  },
  footprint: (document, params, aliases) => {
    const reads = [params.layer, params.source, blendRef(params)];
    const resources = selectionReads(params.selection);
    switch (params.output) {
      case 'layer':
      case 'mask':
        return pixelFootprint(document, aliases, [params.layer], reads, resources);
      case 'new-layer': {
        const blend = resolveRef(blendRef(params), aliases);
        const parent = blend && findLayer(document.root, blend) ? parentKey(document, blend) : undefined;
        const base = pixelFootprint(document, aliases, [], [...reads, ...(parent && parent !== 'root' ? [parent] : [])], resources);
        return { ...base, structural: true };
      }
      case 'selection': {
        const base = pixelFootprint(document, aliases, [], reads, resources);
        return { ...base, reads: [...new Set([...base.reads, DOCUMENT_RESOURCES.selection])], writes: [DOCUMENT_RESOURCES.selection] };
      }
      default: {
        const base = pixelFootprint(document, aliases, [], reads, resources);
        return { ...base, reads: [...base.reads, DOCUMENT_RESOURCES.channels], writes: [DOCUMENT_RESOURCES.channels] };
      }
    }
  },
  validate: (document, params, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = rasterLayer(document, params.layer, context, diagnostics, 'Apply Image');
    const source = layerFor(document, params.source, context, diagnostics);
    if (source && source.kind !== 'raster') diagnostics.push(error('UNSUPPORTED_FOR_KIND', 'Choose a raster source layer.', source.id));
    const blend = params.blendLayer === undefined ? layer : rasterLayer(document, params.blendLayer, context, diagnostics, 'Apply Image source B');
    if (!layer || !blend) return diagnostics;
    if (params.output === 'layer') {
      if (blend.id !== layer.id) diagnostics.push(error('COMMAND_REJECTED', 'Output to the layer requires source B to be that layer. Use a new raster layer to keep a Calculations result.', layer.id));
      checkUnlocked(document, layer, 'pixels', diagnostics, context.policy);
    } else if (params.output === 'mask') {
      if (!layer.mask) diagnostics.push(error('COMMAND_REJECTED', `Layer ${layer.id} has no mask.`, layer.id));
      checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    } else if (params.output === 'new-layer') {
      const location = findLayerParent(document.root, blend.id);
      if (location) checkContainerUnlocked(document, location.parent, diagnostics, blend.id, context.policy);
    } else if (params.output === 'alpha-channel' && alphaChannels(document).length >= MAX_ALPHA_CHANNELS) {
      diagnostics.push(error('LIMIT_EXCEEDED', 'Delete an alpha channel before saving another (8-channel limit).'));
    }
    return diagnostics;
  },
  compile: (document, params, _prepared, aliases) => {
    const layer = requireRaster(document, params.layer, aliases);
    const source = requireRaster(document, params.source, aliases);
    const blend = requireRaster(document, blendRef(params), aliases);
    const settings = applyImageSettings(params);
    const selection = resolveSelection(document, params.selection);
    const blendTransform = documentTransform(document, blend);
    // Clipped to the selection; the test runs in operand B's pixel space.
    const covered = hasSelection(selection) ? (x: number, y: number) => pointIsSelected(selection, transformPoint(blendTransform, { x: x + 0.5, y: y + 0.5 })) : undefined;
    const blended = applyImageStores(source.raster, blend.raster, settings, covered);
    switch (params.output) {
      case 'layer':
        if (blend.id !== layer.id) throw new Error('Output to the layer requires source B to be that layer.');
        return [new ReplaceRasterCommand(layer.id, blended, 'Apply Image')];
      case 'new-layer': {
        const created = createRasterLayer(`${blend.name} ${settings.mode}`, blended.width, blended.height);
        created.raster = blended;
        created.transform = [...blend.transform];
        const location = findLayerParent(document.root, blend.id);
        return [new AddLayerCommand(created, location?.parent.id ?? 'root', location?.index ?? 0)];
      }
      case 'mask':
        return [new ReplaceMaskPixelsCommand(layer.id, grayscaleStore(blended, 'luminosity', false, layer.raster.width, layer.raster.height))];
      default: {
        const regions = projectRegionsToDocument(thresholdRegions(blended, 'luminosity', false), blended, blendTransform);
        if (params.output === 'selection') return [SelectionCommand.regions(regions, 'replace', 'Apply Image to selection')];
        const channels = alphaChannels(document);
        if (channels.length >= MAX_ALPHA_CHANNELS) throw new Error('Delete an alpha channel before saving another (8-channel limit).');
        const calculated = { id: createId('channel'), name: `${source.name} ${settings.channel}`, selection: { regions, inverted: false } };
        return [new SetAlphaChannelsCommand([...channels, calculated], 'Apply Image to alpha channel')];
      }
    }
  },
  describe: (_document, params, aliases) => [{
    layer: params.output === 'layer' || params.output === 'mask' ? resolveRef(params.layer, aliases) ?? params.layer : 'document',
    path: params.output === 'mask' ? 'mask.pixels' : params.output === 'layer' ? 'raster.pixels' : params.output,
    before: null,
    after: { command: 'filters.applyImage', output: params.output },
  }],
});

export const FILTER_COMMANDS = [highPass, matchColor, contentAwareScale, liquify, puppetWarp, warpPerspective, invertPixels, applyImage] as const;
